import { createContext, use, useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { resolveBranding, toBrandingPayload, type BrandingPayload } from "@gatiq/shared";
import { api } from "@/lib/api";
import { applyBranding } from "./applyBranding";

/**
 * The single place branding is fetched. Every route below this provider
 * renders already themed, so no screen or component has per-tenant code.
 *
 * Mounted at `/:orgSlug` (D-03). The platform area renders the same provider
 * with no slug, which resolves to the GatiQ defaults.
 */

interface TenantValue {
  loading: boolean;
  error: string | null;
  payload: BrandingPayload;
}

const PLATFORM_FALLBACK: BrandingPayload = toBrandingPayload(resolveBranding(null, null), null, null);

const TenantContext = createContext<TenantValue>({
  loading: false,
  error: null,
  payload: PLATFORM_FALLBACK,
});

export function useTenant(): TenantValue {
  return use(TenantContext);
}

export function TenantProvider({ children }: { children: ReactNode }): ReactNode {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [state, setState] = useState<TenantValue>({
    loading: Boolean(orgSlug),
    error: null,
    payload: PLATFORM_FALLBACK,
  });

  useEffect(() => {
    let cancelled = false;

    const path = orgSlug ? `/public/orgs/${orgSlug}/branding` : "/public/branding";

    // Paint the platform default immediately so nothing flashes unstyled,
    // then swap to the tenant's theme when it arrives.
    applyBranding(PLATFORM_FALLBACK);
    setState((s) => ({ ...s, loading: true, error: null }));

    api
      .get<BrandingPayload>(path)
      .then((payload) => {
        if (cancelled) return;
        applyBranding(payload);
        setState({ loading: false, error: null, payload });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: err instanceof Error ? err.message : "Could not load this organization.",
          payload: PLATFORM_FALLBACK,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  return <TenantContext value={state}>{children}</TenantContext>;
}
